import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  listFavorites,
  toggleFavorite,
  deleteFavorite,
  type CoreFavorite,
  type FavoriteInput,
} from '@/lib/coreFavorites';

/** Clé unique d'un favori : « type:id ». */
export function favoriteKey(entityType: string, entityId: string | number): string {
  return `${entityType}:${entityId}`;
}

/**
 * Hook favoris du client connecté.
 * Charge la liste, expose l'état d'un élément et permet de basculer / retirer.
 */
export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<CoreFavorite[]>([]);
  const [keys, setKeys] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const rows = await listFavorites(user.id);
      setFavorites(rows);
      setKeys(new Set(rows.map((r) => favoriteKey(r.entity_type, r.entity_id ?? ''))));
    } catch {
      setError('Impossible de charger vos favoris.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { reload(); }, [reload]);

  const isFav = useCallback(
    (entityType: string, entityId: string | number) => keys.has(favoriteKey(entityType, entityId)),
    [keys],
  );

  /** Bascule un favori et renvoie le nouvel état (true = ajouté). */
  const toggle = useCallback(async (input: FavoriteInput) => {
    const on = await toggleFavorite(input);
    await reload();
    return on;
  }, [reload]);

  const remove = useCallback(async (id: number) => {
    await deleteFavorite(id);
    await reload();
  }, [reload]);

  return { favorites, isFav, toggle, remove, reload, loading, error };
}